import React, { useState } from "react";
import { GlassCard } from "../components/ui/UIComponents";
import Step1Basic    from "./steps/Step1Basic";
import Step2Education from "./steps/Step2Education";
import Step3Family   from "./steps/Step3Family";
import Step4Skills   from "./steps/Step4Skills";
import Step5Interests from "./steps/Step5Interests";

// ─────────────────────────────────────────────────────────────────────────────
// FORM PAGE — 5-step multi-step form
// ─────────────────────────────────────────────────────────────────────────────

const STEPS = [
  { label: "Basic Info",   icon: "👤", short: "Basic"    },
  { label: "Education",    icon: "🎓", short: "Education" },
  { label: "Family",       icon: "👨‍👩‍👧", short: "Family"    },
  { label: "Skills",       icon: "💻", short: "Skills"    },
  { label: "Interests",    icon: "🎯", short: "Interests" },
];

export const INITIAL_FORM = {
  // Step 1
  fullName: "", age: "", gender: "", state: "", district: "",
  college: "", areaType: "", nearestMetro: "",
  // Step 2
  qualification: "", courseName: "", collegeName: "", university: "",
  yearCompletion: "", cgpa: "", backlogs: "", studyMode: "",
  // Step 3
  parentIncome: "", familyOccupation: "", financialSupport: "",
  dependents: "", firstGen: "", category: "",
  // Step 4
  techSkills: [], softSkills: [], languages: [],
  experience: "", certification: "", codingLevel: "",
  // Step 5
  interestedField: "", careerGoal: "", workType: [], personality: [],
  relocate: "", targetSalary: "", timeline: "",
};

const NeonBtn = ({ onClick, children, variant = "primary", disabled = false }) => {
  const colors = {
    primary: { c: "#22d3ee", bg: "rgba(34,211,238,0.09)" },
    purple:  { c: "#a855f7", bg: "rgba(168,85,247,0.09)" },
    ghost:   { c: "#6b8aad", bg: "transparent" },
  };
  const { c, bg } = colors[variant];
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: bg, border: `1px solid ${c}`, color: c,
        padding: "11px 28px", borderRadius: 8,
        fontFamily: "Rajdhani, sans-serif", fontSize: 14,
        fontWeight: 700, letterSpacing: 1.5,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        transition: "all 0.2s", textTransform: "uppercase",
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.boxShadow = `0 0 18px ${c}50`; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; }}
    >
      {children}
    </button>
  );
};

export default function FormPage({ form, setForm, onSubmit }) {
  const [step, setStep] = useState(0);

  const progress   = ((step + 1) / STEPS.length) * 100;
  const isFirst    = step === 0;
  const isLast     = step === STEPS.length - 1;

  const stepComponents = [
    <Step1Basic    key={0} form={form} setForm={setForm} />,
    <Step2Education key={1} form={form} setForm={setForm} />,
    <Step3Family   key={2} form={form} setForm={setForm} />,
    <Step4Skills   key={3} form={form} setForm={setForm} />,
    <Step5Interests key={4} form={form} setForm={setForm} />,
  ];

  return (
    <div style={{
      minHeight: "calc(100vh - 64px)",
      background: "#060d1a",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "flex-start",
      padding: "36px 20px 48px",
    }}>
      <div style={{ width: "100%", maxWidth: 640 }}>

        {/* ── Page Title ── */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontFamily: "Rajdhani, sans-serif", fontSize: 11, color: "#22d3ee", letterSpacing: 3, marginBottom: 6 }}>
            AI CAREER ASSESSMENT
          </div>
          <h1 style={{ fontFamily: "Rajdhani, sans-serif", fontSize: 28, fontWeight: 700, color: "#e2f0ff", letterSpacing: 1 }}>
            Build Your Profile
          </h1>
          <p style={{ fontSize: 13, color: "#6b8aad", fontFamily: "Exo 2, sans-serif", marginTop: 6 }}>
            Complete all 5 sections for the most accurate career prediction
          </p>
        </div>

        {/* ── Progress Header ── */}
        <div style={{ marginBottom: 24 }}>
          {/* Step label row */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <span style={{
              fontFamily: "Rajdhani, sans-serif", fontSize: 13,
              color: "#22d3ee", letterSpacing: 1.5,
            }}>
              {STEPS[step].icon} STEP {step + 1} / {STEPS.length} — {STEPS[step].label.toUpperCase()}
            </span>
            <span style={{ fontFamily: "Rajdhani, sans-serif", fontSize: 13, color: "#6b8aad" }}>
              {Math.round(progress)}% Complete
            </span>
          </div>

          {/* Progress bar */}
          <div style={{ height: 5, background: "rgba(34,211,238,0.08)", borderRadius: 5, overflow: "hidden" }}>
            <div style={{
              height: "100%", borderRadius: 5,
              width: `${progress}%`,
              background: "linear-gradient(90deg,#22d3ee,#3b82f6,#a855f7)",
              transition: "width 0.5s ease",
              boxShadow: "0 0 10px rgba(34,211,238,0.4)",
            }} />
          </div>
        </div>

        {/* ── Step Tab Bar ── */}
        <div style={{
          display: "grid",
          gridTemplateColumns: `repeat(${STEPS.length}, 1fr)`,
          gap: 6, marginBottom: 22,
        }}>
          {STEPS.map((s, i) => {
            const done   = i < step;
            const active = i === step;
            return (
              <button
                key={s.short}
                onClick={() => i < step && setStep(i)}
                style={{
                  padding: "7px 4px", textAlign: "center",
                  fontFamily: "Rajdhani, sans-serif", fontSize: 11,
                  letterSpacing: 0.5, textTransform: "uppercase",
                  background: active ? "rgba(34,211,238,0.13)"
                    : done   ? "rgba(34,211,238,0.05)" : "transparent",
                  border: `1px solid ${active ? "#22d3ee"
                    : done ? "rgba(34,211,238,0.25)" : "rgba(34,211,238,0.08)"}`,
                  borderRadius: 7,
                  color: active ? "#22d3ee" : done ? "#4b7a9a" : "#2d4a66",
                  cursor: i < step ? "pointer" : "default",
                  transition: "all 0.2s", whiteSpace: "nowrap",
                }}
              >
                {done ? "✓ " : s.icon + " "}{s.short}
              </button>
            );
          })}
        </div>

        {/* ── Step Card ── */}
        <GlassCard key={step} animate style={{ marginBottom: 22 }}>
          <h2 style={{
            fontFamily: "Rajdhani, sans-serif",
            fontSize: 20, fontWeight: 700,
            color: "#e2f0ff", marginBottom: 20,
            letterSpacing: 0.5,
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <span>{STEPS[step].icon}</span> {STEPS[step].label}
          </h2>
          {stepComponents[step]}
        </GlassCard>

        {/* ── Navigation Buttons ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <NeonBtn
            variant="ghost"
            onClick={() => setStep(s => s - 1)}
            disabled={isFirst}
          >
            ← Back
          </NeonBtn>

          {/* Step dots */}
          <div style={{ display: "flex", gap: 6 }}>
            {STEPS.map((_, i) => (
              <div key={i} style={{
                width: i === step ? 20 : 7,
                height: 7, borderRadius: 4,
                background: i === step ? "#22d3ee"
                  : i < step ? "#22d3ee55" : "rgba(34,211,238,0.12)",
                transition: "all 0.3s",
              }} />
            ))}
          </div>

          {isLast ? (
            <NeonBtn variant="purple" onClick={onSubmit}>
              🚀 Generate AI Report
            </NeonBtn>
          ) : (
            <NeonBtn onClick={() => setStep(s => s + 1)}>
              Next Step →
            </NeonBtn>
          )}
        </div>
      </div>
    </div>
  );
}